// Long-rest completion is one pure plan. Its resource, status and charge writes are
// saved together by the additive resource-truth long_rest kind, never as separate clicks.
import { ftCampaignTimeAddSeconds, type CampaignCalendarEmbed, type CampaignClockCols } from '../../shared/campaign-clock.js';
import { abilityEntryId, CHARGE_BUCKETS, type ChargeReaders } from '../charges/charge-adjust.js';
import { heldLayerlessDefinition } from '../compile/held-definition.js';
import { TRIGGER_CARRIERS, type TriggerCarrier } from '../reactions/trigger-dispatch.js';
import { statusRows } from '../statuses/expiry.js';
import { skipUnported, type UnportedSkipRow } from '../unported.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const numeric = (v: unknown): number | null => { const x = scalar(v); return (typeof x === 'number' || typeof x === 'string' && x.trim() !== '') && Number.isFinite(Number(x)) ? Math.trunc(Number(x)) : null; };
const int = (v: unknown): number => numeric(v) ?? 0;
const inactivePassive = (entry: Row | null): boolean => {
  if (!entry || !('passive_conditions_active' in entry)) return false;
  const stamp = scalar(entry.passive_conditions_active); return !stamp || stamp === '0';
};

export const LONG_REST_IDS = {
  resting: '3188FB3B-C860-43D4-9BA0-40364C460385', sleeping: '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B',
  prone: 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', awake: '5AA76DEE-CE52-E021-67CD-8C701DA0E56F',
  fullyRested: '80585488-CC4B-4A80-8ECE-CB1CE83F2407', exhausted: 'EB6FB558-7427-A647-8F46-D2E8C25F1F60',
  maimed: 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D', blighted: '4744C91C-51DC-4C36-8C6E-DB801410FF61',
  fatigue: 'CEF623E6-6A71-37FD-29C7-12988B43151A', aether: '5FF2F49A-7667-D686-2BC6-32623375A091',
} as const;
export interface LongRestRemoval {
  status_id: string; target_id: string;
  before: { status_item_id: string; status_target_id: string; status_caster_id: string; status_stack: number };
}
export interface LongRestStatuses {
  remove: LongRestRemoval[];
  stack: Array<{ status_id: string; target_id: string; from: number; to: number }>;
  add: Row[];
}
export interface LongRestCharge { charge_id: string; kind: 'ability' | 'inventory'; holder: string; item_id: string; from: number | null; to: number }
export interface LongRestPlan {
  before: Record<string, number | null>; after: Record<string, number>;
  statuses: LongRestStatuses; charges: LongRestCharge[]; rows: UnportedSkipRow[];
  /** False means completion was skipped as a whole; the boundary still runs. */
  completed: boolean;
}
export interface LongRestReaders extends ChargeReaders {
  definition: (id: string) => Row | null;
  /** Retired quantity aliases still resolve to the family, never stage-machine swaps. */
  statusBase?: (id: string) => { base: string; count: number | null } | null;
}
export interface LongRestInput {
  row: Row; clock: CampaignClockCols; calendar: CampaignCalendarEmbed;
  playerControlledSummon: boolean;
}
const blank = (): LongRestPlan => ({ before: {}, after: {}, statuses: { remove: [], stack: [], add: [] }, charges: [], rows: [], completed: false });
export const AFTER_LONG_REST = 'triggers_after_long_rest';
export interface LongRestListener { name: string; id: string; definition: Row | null; carrier: TriggerCarrier | null; entry: Row | null; statusId?: string }
/** ft_server_skip_listeners() for triggers_after_long_rest: who would fire. An unheld definition is named, never read as "no listener". */
export function longRestListeners(row: Row, readers: LongRestReaders, removed = new Set<string>()): LongRestListener[] {
  const listeners: LongRestListener[] = [];
  const listens = (definition: Row): boolean => int(readers.value(definition, AFTER_LONG_REST)) !== 0;
  const name = (definition: Row, id: string): string => String(scalar(record(definition.items)?.item_name) ?? '') || id;
  // An inline shell with layers is the definition; otherwise the held catalog row; a held layer-less shell has no listener.
  const resolve = (inline: Row | null, id: string): Row | 'layerless' | null => {
    const layers = record(inline?.item_layer_types);
    if (inline && layers && Object.keys(layers).length) return inline;
    return (id ? readers.definition(id) : null) ?? (heldLayerlessDefinition(inline) ? 'layerless' : null);
  };
  // The creature's own explicit slot only; the reader must not hydrate default providers here.
  const own = record(row.item_layer_types);
  if (own && AFTER_LONG_REST in own && int(readers.value(row, AFTER_LONG_REST)) !== 0) listeners.push({ name: 'creature definition', id: '', definition: null, carrier: null, entry: null });
  const abilities = record(record(record(row.computed)?.creature)?.abilities);
  // Explicit copies can carry an inactive stamp shadowed by the computed reader's early return.
  // Read just those slots, without crawling ungated class/ancestry providers again.
  const pool = TRIGGER_CARRIERS.flatMap(carrier => [...readers.entries(row, carrier),
    ...readers.entries({ item_layer_types: { [carrier]: own?.[carrier] } }, carrier), ...readers.entries({ [carrier]: row[carrier] }, carrier),
    ...(Array.isArray(abilities?.[carrier]) ? abilities![carrier] as unknown[] : [])].map(entry => ({ carrier, entry })));
  // One ability may sit in both the layer bucket and the compiled list: an explicit inactive stamp on either copy wins, any
  // resolved copy that listens names it, and it is unknown only when no copy resolves at all.
  const copies = new Map<string, Array<{ definition: Row | 'layerless' | null; carrier: TriggerCarrier; entry: Row }>>();
  const inactive = new Set<string>();
  for (const { entry, carrier } of pool) {
    const id = abilityEntryId(entry);
    if (!id) continue;
    if (inactivePassive(record(entry))) inactive.add(id);
    copies.set(id, [...copies.get(id) ?? [], { definition: resolve(record(record(entry)?.item_arr) ?? record(entry), id), carrier, entry: record(entry) ?? { item_id: id } }]);
  }
  for (const [id, resolved] of copies) {
    if (inactive.has(id)) continue;
    const listener = resolved.find(d => d.definition !== null && d.definition !== 'layerless' && listens(d.definition));
    if (listener) listeners.push({ name: name(listener.definition as Row, id), id, definition: listener.definition as Row, carrier: listener.carrier, entry: listener.entry });
    else if (resolved.every(d => d.definition === null)) listeners.push({ name: 'definition not held: ' + id, id, definition: null, carrier: null, entry: null });
  }
  for (const value of statusRows(row, 'target')) {
    const status = record(value);
    if (!status || int(status.status_deleted) === 1 || removed.has(upper(status.status_id))) continue;
    const shell = record(status.status_arr), itemId = upper(status.status_item_id ?? record(shell?.items)?.item_id);
    const definition = resolve(shell, itemId);
    if (!definition) listeners.push({ name: 'status definition not held: ' + (itemId || upper(status.status_id)), id: itemId, definition: null, carrier: null, entry: status });
    else if (definition !== 'layerless' && listens(definition)) listeners.push({ name: name(definition, itemId), id: itemId, definition, carrier: null, entry: status, statusId: upper(status.status_id) });
  }
  return listeners;
}
export function skippedLongRest(reason: string, actorId = ''): LongRestPlan {
  return { ...blank(), rows: [skipUnported('long-rest:' + reason, { actorId })] };
}
/** PHP complete_long_rest's party completion, corrected to the authoritative quantity-stack contract. */
export function resolveLongRest(input: LongRestInput, readers: LongRestReaders, uuid: () => string): LongRestPlan {
  const row = input.row, ec = record(row.encounter_creatures) ?? {}, actorId = upper(ec.encounter_creature_id);
  const vitality = record(record(row.computed)?.vitality) ?? {};
  const spirit = numeric(readers.value(row, 'spirit'));
  const current = numeric(vitality.v_current), maximum = numeric(vitality.effective_v_max);
  if (!actorId || spirit === null || current === null || maximum === null) return skippedLongRest('resource-input-unavailable', actorId);
  const fullyRested = readers.definition(LONG_REST_IDS.fullyRested), awake = readers.definition(LONG_REST_IDS.awake);
  const plan = blank();
  let level = numeric(readers.value(row, 'total_level'));
  if (level === null && input.playerControlledSummon) level = numeric(readers.value(row, 'cr'));
  if (['mana', 'v_current', 'v_temp'].some(key => ec[key] === undefined)) return skippedLongRest('raw-resource-state-unavailable', actorId);
  const raw = (key: string): number | null => ec[key] === null ? null : int(ec[key]);
  plan.before = { mana: raw('mana'), v_current: raw('v_current'), v_temp: raw('v_temp') };
  plan.after = { mana: spirit, v_current: Math.max(0, Math.min(maximum, current + Math.max(0, level ?? 0) * 2)), v_temp: 0 };
  let keepsLuck = false;
  const charged = new Map<string, unknown>();
  for (const bucket of CHARGE_BUCKETS) for (const value of readers.entries(row, bucket)) if (!charged.has(abilityEntryId(value))) charged.set(abilityEntryId(value), value);
  for (const entry of readers.entries(row, 'passive_ability')) {
    const e = record(entry);
    if (inactivePassive(e)) continue;
    const id = abilityEntryId(entry), inline = record(e?.item_arr);
    const definition = inline && record(inline.item_layer_types) ? inline : readers.definition(id);
    if (!definition) return skippedLongRest('passive-definition-unavailable', actorId);
    if (int(readers.value(definition, 'luck_persists_after_long_rest')) === 1) keepsLuck = true;
  }
  if (!keepsLuck) {
    if (ec.luck === undefined) return skippedLongRest('raw-luck-state-unavailable', actorId);
    plan.before.luck = raw('luck'); plan.after.luck = 0;
  }
  let statusUnsupportedReason: string | null = !fullyRested || !awake ? 'status-definition-unavailable' : null;
  for (const status of statusRows(row, 'target')) {
    if (int(status.status_deleted) === 1) continue;
    const itemId = upper(status.status_item_id ?? record(record(status.status_arr)?.items)?.item_id);
    const id = upper(status.status_id); if (!id) { statusUnsupportedReason ??= 'status-identity-unavailable'; continue; }
    const family = readers.statusBase?.(itemId), base = upper(family?.base ?? itemId);
    const rawStack = numeric(status.status_stack);
    const stack = rawStack ?? (status.status_stack === undefined && row.raw_status_snapshot !== true ? 1 : null);
    const unusable = stack === null || row.raw_status_snapshot === true && (!upper(status.status_item_id) || !upper(status.status_target_id) || !upper(status.status_caster_id));
    const remove = (): void => {
      if (unusable) { statusUnsupportedReason ??= 'raw-status-state-unavailable'; return; }
      plan.statuses.remove.push({ status_id: id, target_id: actorId,
      before: { status_item_id: itemId, status_target_id: upper(status.status_target_id) || actorId,
        status_caster_id: status.status_caster_id === undefined ? actorId : upper(status.status_caster_id), status_stack: stack! } }); };
    if ([LONG_REST_IDS.resting, LONG_REST_IDS.sleeping, LONG_REST_IDS.prone, LONG_REST_IDS.awake,
      LONG_REST_IDS.exhausted, LONG_REST_IDS.fullyRested, LONG_REST_IDS.fatigue].includes(base as never)
      || scalar(status.status_casting_targets_arr) === '__janus_contest_success_immunity_until_long_rest__') remove();
    else if (base === LONG_REST_IDS.maimed || base === LONG_REST_IDS.blighted) {
      if (unusable) { statusUnsupportedReason ??= 'raw-status-state-unavailable'; continue; }
      const from = stack!;
      if (base !== itemId) { statusUnsupportedReason ??= 'quantity-alias-row-unported'; continue; }
      if (from <= 1) remove(); else plan.statuses.stack.push({ status_id: id, target_id: actorId, from, to: from - 1 });
    }
  }
  const rawCharges = Array.isArray(row.charges) ? row.charges : [];
  let invalidRawCharge = false;
  const skipCount = (kind: LongRestCharge['kind'], holder: string, itemId: string, reason = 'charge-count-unavailable'): void => {
    // Detail events are transient; persist the exact pool identity in the GM reason itself.
    plan.rows.push(skipUnported(`long-rest:${reason}:${kind}:${holder}:${itemId}`, { actorId, kind, holder, itemId }));
  };
  const currentCount = (holder: string, itemId: string, compiled: number | null): number | null => {
    const raw = rawCharges.map(record).find(c => upper(c?.charge_holder) === holder && upper(c?.item_id) === itemId && int(c?.charge_deleted) === 0);
    return row.raw_charge_snapshot === true && raw ? numeric(raw.charges) : compiled;
  };
  const charge = (kind: LongRestCharge['kind'], holder: string, itemId: string, from: number, to: number): void => {
    if (from === to) return;
    const raw = rawCharges.map(record).find(c => upper(c?.charge_holder) === holder && upper(c?.item_id) === itemId);
    const rawKnown = row.raw_charge_snapshot === true;
    if (rawKnown && raw && int(raw.charge_deleted) === 0 && numeric(raw.charges) === null) { invalidRawCharge = true; return; }
    const savedFrom = rawKnown ? raw && int(raw.charge_deleted) === 0 ? numeric(raw.charges) : null : from;
    plan.charges.push({ kind, holder, item_id: itemId, charge_id: upper(raw?.charge_id) || upper(uuid()), from: savedFrom, to });
  };
  for (const [itemId, value] of charged) {
    if (!itemId) continue;
    const entry = record(value), fields = record(entry?.charges), inline = record(entry?.item_arr);
    const definition = inline && record(inline.item_layer_types) ? inline : readers.definition(itemId);
    if (!definition && fields?.resets_long_rest === undefined && entry?.ability_resets_long_rest === undefined) return skippedLongRest('charge-definition-unavailable', actorId);
    const definitionReset = definition ? readers.value(definition, 'ability_resets_long_rest') : null;
    const reset = scalar(fields?.resets_long_rest ?? entry?.ability_resets_long_rest ?? (definitionReset !== null && definitionReset !== undefined ? 1 : 0));
    const from = currentCount(actorId, itemId, numeric(fields?.current ?? entry?.ability_charges));
    if (itemId === LONG_REST_IDS.aether) {
      if (inactivePassive(entry)) continue;
      if (from === null) { skipCount('ability', actorId, itemId, 'aether-count-unavailable'); continue; }
      charge('ability', actorId, itemId, from, 0); continue;
    }
    if (int(reset) !== 1) continue;
    if (from === null) { skipCount('ability', actorId, itemId); continue; }
    const max = numeric(fields?.max ?? entry?.max_ability_charges ?? (definition ? readers.value(definition, 'ability_charges') : null));
    if (from !== null && max !== null && max > 0 && from < max) charge('ability', actorId, itemId, from, max);
  }
  const walk = (values: unknown[]): void => {
    for (const value of values) {
      const inventory = record(value); if (!inventory) continue;
      const bucket = record(inventory.inventory) ?? {}, holder = upper(bucket.inventory_id);
      const itemId = upper(bucket.item_id ?? record(inventory.items)?.item_id);
      const from = currentCount(holder, itemId, numeric(readers.value(inventory, 'current_item_charges'))), max = numeric(readers.value(inventory, 'item_charges'));
      if (int(readers.value(inventory, 'item_rechargeable')) === 1 && holder && itemId) {
        if (from === null) skipCount('inventory', holder, itemId);
        else if (max !== null && max > 0 && from < max) charge('inventory', holder, itemId, from, max);
      }
      if (Array.isArray(inventory.inventory_in_container)) walk(inventory.inventory_in_container);
    }
  };
  walk(readers.entries(row, 'inventory'));
  if (invalidRawCharge) return skippedLongRest('raw-charge-count-unavailable', actorId);
  const ends = ftCampaignTimeAddSeconds(input.clock, input.calendar, 8 * 3600);
  const add = (itemId: string, definition: Row, end: CampaignClockCols | null): void => {
    const status: Row = { status_id: upper(uuid()), status_item_id: itemId, status_target_id: actorId, status_caster_id: actorId,
      status_connected_id: upper(uuid()), status_origin_id: '', status_casting_targets_arr: '', status_stack: 1, status_private: 0, status_deleted: 0,
      status_arr: definition };
    for (const unit of ['year', 'month', 'day', 'hour', 'minute', 'second'] as const) {
      status['status_starts_campaign_' + unit] = input.clock[unit]; status['status_ends_campaign_' + unit] = end?.[unit] ?? 0;
    }
    plan.statuses.add.push(status);
  };
  if (fullyRested && awake) { add(LONG_REST_IDS.fullyRested, fullyRested, ends); add(LONG_REST_IDS.awake, awake, null); }
  if (statusUnsupportedReason) {
    plan.statuses = { remove: [], stack: [], add: [] };
    plan.rows.push(skipUnported('long-rest:' + statusUnsupportedReason, { actorId }));
  }
  // Trigger effects are decided after every rest in this boundary has committed.
  plan.completed = true;
  return plan;
}
